/**
 * useWebSocketRecorder Hook
 *
 * Handles real-time voice recording with WebSocket streaming and live transcription.
 */

import { useEffect, useRef, useState, useCallback } from 'react';

interface UseWebSocketRecorderProps {
  meetingId: number;
  language?: string;
  onTranscriptionUpdate: (text: string, confidence: number) => void;
  onError: (error: Error) => void;
  onStatusChange: (status: RecordingStatus) => void;
}

export type RecordingStatus =
  | 'idle'
  | 'connecting'
  | 'connected'
  | 'recording'
  | 'paused'
  | 'stopping'
  | 'stopped'
  | 'error';

export interface TranscriptionUpdate {
  text: string;
  confidence: number;
  sequence: number;
  timestamp: string;
}

export const useWebSocketRecorder = ({
  meetingId,
  language = 'he',
  onTranscriptionUpdate,
  onError,
  onStatusChange
}: UseWebSocketRecorderProps) => {
  const [status, setStatus] = useState<RecordingStatus>('idle');
  const [duration, setDuration] = useState(0);
  const [transcriptParts, setTranscriptParts] = useState<TranscriptionUpdate[]>([]);

  const wsRef = useRef<WebSocket | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const sequenceRef = useRef(0);
  const startTimeRef = useRef<number>(0);
  const durationIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isRecordingRef = useRef<boolean>(false);

  // WebSocket URL
  const getWebSocketUrl = useCallback(() => {
    const token = localStorage.getItem('token');
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';

    // Use environment variable or fallback to replacing port
    const apiUrl = process.env.REACT_APP_API_URL || '';
    let host = '';

    console.log('[WS DEBUG] Environment check:', {
      apiUrl,
      windowLocation: window.location.href,
      currentProtocol: window.location.protocol,
      websocketProtocol: protocol,
      host: window.location.host,
      isSecure: window.location.protocol === 'https:'
    });

    if (apiUrl) {
      // Extract host from API URL (e.g., "http://localhost:8000/api/v1" -> "localhost:8000")
      const urlMatch = apiUrl.match(/^https?:\/\/([^/]+)/);
      if (urlMatch) {
        host = urlMatch[1];
        console.log('[WS DEBUG] Extracted host from API URL:', host);
      }
    }

    if (!host) {
      // In AWS/production, use same host as frontend (no port replacement needed)
      if (window.location.host.includes('localhost') || window.location.host.includes(':3000')) {
        // Development: replace port
        host = window.location.host.replace(':3000', ':8000');
        console.log('[WS DEBUG] Using development fallback host:', host);
      } else {
        // Production: use same host as frontend
        host = window.location.host;
        console.log('[WS DEBUG] Using production host (same as frontend):', host);
      }
    }

    // Try API Gateway WebSocket path if ALB has issues
    const isProduction = !host.includes('localhost') && !host.includes(':8000');
    let wsUrl;

    if (isProduction) {
      // In production, try using the same path structure as API endpoints
      wsUrl = `${protocol}//${host}/api/v1/ws/record/${meetingId}?token=${token}&language=${language}`;
      console.log('[WS DEBUG] Using production API path for WebSocket');
    } else {
      // Development - direct WebSocket path
      wsUrl = `${protocol}//${host}/ws/record/${meetingId}?token=${token}&language=${language}`;
      console.log('[WS DEBUG] Using development WebSocket path');
    }

    console.log('[WS DEBUG] Generated WebSocket URL:', wsUrl);
    console.log('[WS DEBUG] Token present:', !!token);

    if (!token) {
      console.error('[WS DEBUG] No authentication token found!');
    }

    return wsUrl;
  }, [meetingId, language]);

  // Update status
  const updateStatus = useCallback((newStatus: RecordingStatus) => {
    setStatus(newStatus);
    onStatusChange(newStatus);
  }, [onStatusChange]);

  // Duration timer functions
  const startDurationTimer = () => {
    if (!startTimeRef.current) {
      startTimeRef.current = Date.now();
    }

    durationIntervalRef.current = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
      setDuration(elapsed);
    }, 1000);
  };

  const stopDurationTimer = useCallback(() => {
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }
  }, []);

  // Handle transcription update
  const handleTranscriptionUpdate = useCallback((data: any) => {
    const update: TranscriptionUpdate = {
      text: data.text,
      confidence: data.confidence,
      sequence: data.chunk_sequence,
      timestamp: data.timestamp
    };

    // Since we're sending the complete transcript each time (incremental updates),
    // replace the entire transcript rather than appending
    setTranscriptParts([update]);
    onTranscriptionUpdate(data.text, data.confidence);
  }, [onTranscriptionUpdate]);

  // Handle server messages
  const handleServerMessage = useCallback((data: any) => {
    console.log('Received server message:', data.type);

    switch (data.type) {
      case 'connection_established':
        console.log('[WS CLIENT] Session established:', data.session_id);
        updateStatus('connected');
        break;

      case 'recording_started':
        updateStatus('recording');
        isRecordingRef.current = true;
        startDurationTimer();
        break;

      case 'recording_paused':
        updateStatus('paused');
        isRecordingRef.current = false;
        stopDurationTimer();
        break;

      case 'recording_resumed':
        updateStatus('recording');
        isRecordingRef.current = true;
        startDurationTimer();
        break;

      case 'transcription_update':
        handleTranscriptionUpdate(data);
        break;

      case 'transcription_complete':
        console.log('Transcription complete:', data);
        updateStatus('stopped');
        stopDurationTimer();
        break;

      case 'processing_status':
        console.log('Processing status:', data);
        break;

      case 'error':
        console.error('Server error:', data.message);
        onError(new Error(data.message));
        if (data.severity === 'error') {
          updateStatus('error');
        }
        break;

      case 'pong':
        // Keep-alive response
        break;

      default:
        console.warn('Unknown message type:', data.type);
    }
  }, [updateStatus, onError, handleTranscriptionUpdate, stopDurationTimer]);

  // Connect WebSocket
  const connect = useCallback(async () => {
    try {
      updateStatus('connecting');

      const wsUrl = getWebSocketUrl();
      console.log('Connecting to WebSocket:', wsUrl);

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      // Set a connection timeout
      const connectionTimeout = setTimeout(() => {
        if (ws.readyState === WebSocket.CONNECTING) {
          console.error('[WS CLIENT] Connection timeout - closing WebSocket');
          ws.close();
          onError(new Error('WebSocket connection timeout'));
          updateStatus('error');
        }
      }, 10000); // 10 second timeout

      ws.onopen = () => {
        clearTimeout(connectionTimeout);
        console.log('[WS CLIENT] WebSocket connected - waiting for server confirmation');
        // Don't set to 'connected' yet - wait for connection_established message
      };

      ws.onmessage = (event) => {
        console.log('[WS CLIENT] Received message:', event.data);
        try {
          const data = JSON.parse(event.data);
          console.log('[WS CLIENT] Parsed message:', data);
          handleServerMessage(data);
        } catch (error) {
          console.error('[WS CLIENT] Error parsing message:', error);
        }
      };

      ws.onerror = (error) => {
        clearTimeout(connectionTimeout);
        console.error('[WS CLIENT] WebSocket error:', error);
        console.error('[WS CLIENT] WebSocket state:', ws.readyState);
        console.error('[WS CLIENT] WebSocket URL was:', wsUrl);

        let errorMessage = 'WebSocket connection error';
        if (ws.readyState === WebSocket.CLOSED) {
          errorMessage = 'WebSocket connection was rejected or closed by server';
        } else if (ws.readyState === WebSocket.CLOSING) {
          errorMessage = 'WebSocket connection is being closed';
        }

        onError(new Error(errorMessage));
        updateStatus('error');
      };

      ws.onclose = (event) => {
        clearTimeout(connectionTimeout);
        console.log('[WS CLIENT] WebSocket closed:', {
          code: event.code,
          reason: event.reason,
          wasClean: event.wasClean,
          url: wsUrl,
          readyState: ws.readyState
        });

        // Log specific close codes for debugging
        switch (event.code) {
          case 1000:
            console.log('[WS CLIENT] Normal closure');
            break;
          case 1001:
            console.log('[WS CLIENT] Going away');
            break;
          case 1002:
            console.log('[WS CLIENT] Protocol error');
            break;
          case 1003:
            console.log('[WS CLIENT] Unsupported data');
            break;
          case 1006:
            console.error('[WS CLIENT] Abnormal closure - connection lost');
            break;
          case 1011:
            console.error('[WS CLIENT] Server error');
            break;
          case 4001:
            console.error('[WS CLIENT] Unauthorized - invalid token');
            break;
          case 4003:
            console.error('[WS CLIENT] Forbidden - access denied');
            break;
          case 4004:
            console.error('[WS CLIENT] Not found - meeting not found');
            break;
          default:
            console.log('[WS CLIENT] Close code:', event.code);
        }

        if (status === 'recording' || status === 'paused') {
          // Unexpected disconnect
          const reason = event.reason || `Connection closed with code ${event.code}`;
          onError(new Error(`Connection lost: ${reason}`));
        }
        updateStatus('stopped');
      };

    } catch (error) {
      onError(error as Error);
      updateStatus('error');
    }
  }, [getWebSocketUrl, updateStatus, onError, status, handleServerMessage]);

  // Check microphone permission status
  const checkMicrophonePermission = async (): Promise<boolean> => {
    try {
      // Check if permissions API is available
      if ('permissions' in navigator) {
        const permission = await navigator.permissions.query({ name: 'microphone' as PermissionName });
        console.log('[MIC DEBUG] Permission state:', permission.state);
        return permission.state === 'granted';
      }
      return false;
    } catch (error) {
      console.log('[MIC DEBUG] Permissions API not available or failed');
      return false;
    }
  };

  // Start recording
  const startRecording = useCallback(async () => {
    try {
      console.log('[MIC DEBUG] Starting recording request...');

      // Check current permission state
      const hasPermission = await checkMicrophonePermission();
      console.log('[MIC DEBUG] Has permission:', hasPermission);

      // Check if getUserMedia is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Microphone access is not supported in this browser');
      }

      // Check HTTPS requirement
      if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
        throw new Error('Microphone access requires HTTPS connection');
      }

      console.log('[MIC DEBUG] Requesting microphone access...');

      // Request microphone access with mobile-optimized settings
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          // More compatible settings for mobile
          sampleRate: { ideal: 48000, min: 16000, max: 48000 },
          channelCount: { ideal: 1 }
        }
      });

      console.log('[MIC DEBUG] Microphone access granted successfully');

      streamRef.current = stream;

      // Determine supported MIME type
      const mimeType = getSupportedMimeType();
      console.log('Using MIME type:', mimeType);

      // Create MediaRecorder with timeslice for chunked recording
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType,
        audioBitsPerSecond: 64000  // 64kbps for speech
      });

      mediaRecorderRef.current = mediaRecorder;
      sequenceRef.current = 0;

      // Handle audio chunks
      mediaRecorder.ondataavailable = async (event) => {
        console.log('[WS CLIENT] Audio chunk available, size:', event.data.size, 'bytes', 'isRecording:', isRecordingRef.current);
        if (event.data.size > 0 && isRecordingRef.current) {
          await sendAudioChunk(event.data);
        } else if (event.data.size === 0) {
          console.warn('[WS CLIENT] Empty audio chunk received');
        } else if (!isRecordingRef.current) {
          console.log('[WS CLIENT] Skipping chunk - not recording');
        }
      };

      // Send start message
      wsRef.current?.send(JSON.stringify({
        type: 'start_recording',
        language,
        metadata: {
          sample_rate: 48000,
          channels: 1,
          codec: mimeType.split(';')[0].split('/')[1]
        }
      }));

      // Start recording with 2-second chunks
      mediaRecorder.start(2000);  // timeslice = 2000ms
      console.log('MediaRecorder started');

    } catch (error) {
      console.error('[MIC DEBUG] Error starting recording:', error);

      let userFriendlyMessage = 'Failed to start recording';

      if (error instanceof Error) {
        const errorMessage = error.message.toLowerCase();

        if (errorMessage.includes('permission denied') || errorMessage.includes('not allowed')) {
          userFriendlyMessage = 'Microphone permission denied. Please allow microphone access and try again.';
        } else if (errorMessage.includes('not found') || errorMessage.includes('no devices')) {
          userFriendlyMessage = 'No microphone found. Please check your device has a microphone.';
        } else if (errorMessage.includes('not readable') || errorMessage.includes('in use')) {
          userFriendlyMessage = 'Microphone is being used by another application. Please close other apps and try again.';
        } else if (errorMessage.includes('not supported') || errorMessage.includes('https')) {
          userFriendlyMessage = error.message; // Use our custom HTTPS message
        } else if (errorMessage.includes('aborted')) {
          userFriendlyMessage = 'Recording was cancelled. Please try again.';
        } else if (errorMessage.includes('overconstrained')) {
          userFriendlyMessage = 'Your device microphone doesn\'t support the required settings. Please try with a different device.';
        }

        // Enhanced mobile-specific error handling
        const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        if (isMobile && (errorMessage.includes('permission') || errorMessage.includes('not allowed'))) {
          userFriendlyMessage += '\n\nOn mobile:\n• Tap the microphone icon in your browser\'s address bar\n• Select "Allow" for microphone access\n• Refresh the page and try again';
        }
      }

      console.log('[MIC DEBUG] User-friendly error:', userFriendlyMessage);
      onError(new Error(userFriendlyMessage));
      updateStatus('error');
    }
  }, [language, updateStatus, onError]);

  // Send audio chunk
  const sendAudioChunk = async (blob: Blob) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.warn('WebSocket not ready, skipping chunk');
      return;
    }

    sequenceRef.current++;

    // Send header (JSON)
    const header = {
      type: 'audio_chunk',
      sequence: sequenceRef.current,
      timestamp: Date.now(),
      duration: 2000,  // 2 seconds
      is_final: false
    };

    wsRef.current.send(JSON.stringify(header));
    console.log(`[WS CLIENT] Sent audio header ${sequenceRef.current}`);

    // Send audio data (binary)
    const arrayBuffer = await blob.arrayBuffer();
    wsRef.current.send(arrayBuffer);

    console.log(`[WS CLIENT] Sent audio chunk ${sequenceRef.current}, size: ${arrayBuffer.byteLength} bytes`);
  };

  // Get supported MIME type
  const getSupportedMimeType = (): string => {
    const types = [
      'audio/mp4',  // Try MP4 first - each chunk should be self-contained
      'audio/ogg;codecs=opus',
      'audio/webm;codecs=opus',
      'audio/webm'
    ];

    const supported = types.find(type => MediaRecorder.isTypeSupported(type));
    console.log('[WS CLIENT] Supported MIME types:', types.filter(t => MediaRecorder.isTypeSupported(t)));
    console.log('[WS CLIENT] Selected MIME type:', supported || 'audio/webm');
    return supported || 'audio/webm';
  };

  // Pause recording
  const pauseRecording = useCallback(() => {
    if (mediaRecorderRef.current && status === 'recording') {
      mediaRecorderRef.current.pause();

      wsRef.current?.send(JSON.stringify({
        type: 'pause_recording'
      }));

      console.log('Recording paused');
    }
  }, [status]);

  // Resume recording
  const resumeRecording = useCallback(() => {
    if (mediaRecorderRef.current && status === 'paused') {
      mediaRecorderRef.current.resume();

      wsRef.current?.send(JSON.stringify({
        type: 'resume_recording'
      }));

      console.log('Recording resumed');
    }
  }, [status]);

  // Stop recording
  const stopRecording = useCallback(async (save: boolean = true) => {
    updateStatus('stopping');
    isRecordingRef.current = false;
    console.log('Stopping recording, save:', save);

    // Stop MediaRecorder
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current = null;
    }

    // Stop stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    // Send stop message
    wsRef.current?.send(JSON.stringify({
      type: 'stop_recording',
      save
    }));

    stopDurationTimer();

    // Close WebSocket after a delay (wait for final messages)
    setTimeout(() => {
      wsRef.current?.close();
      wsRef.current = null;
    }, 2000);

  }, [updateStatus, stopDurationTimer]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopDurationTimer();

      if (mediaRecorderRef.current) {
        mediaRecorderRef.current.stop();
      }

      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }

      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [stopDurationTimer]);

  // Reset function to clear all state
  const reset = useCallback(() => {
    // Stop any ongoing recording
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current = null;
    }

    // Stop media stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    // Close WebSocket
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.close();
    }
    wsRef.current = null;

    // Stop duration timer
    stopDurationTimer();

    // Reset state
    setStatus('idle');
    setDuration(0);
    setTranscriptParts([]);
    isRecordingRef.current = false;
    sequenceRef.current = 0;
    startTimeRef.current = 0;
  }, [stopDurationTimer]);

  // Request microphone permission (can be called before recording)
  const requestMicrophonePermission = useCallback(async (): Promise<boolean> => {
    try {
      console.log('[MIC DEBUG] Proactively requesting microphone permission...');

      // Check if already granted
      const hasPermission = await checkMicrophonePermission();
      if (hasPermission) {
        console.log('[MIC DEBUG] Permission already granted');
        return true;
      }

      // Request permission by attempting to get user media then releasing it
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: { ideal: 48000, min: 16000, max: 48000 },
          channelCount: { ideal: 1 }
        }
      });

      // Release the stream immediately
      stream.getTracks().forEach(track => track.stop());
      console.log('[MIC DEBUG] Permission granted and stream released');
      return true;

    } catch (error) {
      console.error('[MIC DEBUG] Permission request failed:', error);
      return false;
    }
  }, []);

  return {
    status,
    duration,
    transcriptParts,
    fullTranscript: transcriptParts.map(p => p.text).join(' '),
    connect,
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    reset,
    requestMicrophonePermission
  };
};
